import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarJson, pedirJson } from "./api";

/* Aprovação condicional do chamado: os pedidos que o chamado abre, a decisão de quem aprova e as
   regras que a administração mantém. As consultas do chamado começam em `["tickets", id]`; decidir
   invalida `["tickets"]` inteiro, porque a recusa cancela o chamado e a decisão mexe no SLA. */

const CHAVE = ["tickets"] as const;
const CHAVE_DAS_REGRAS = ["aprovacoes", "regras"] as const;
const chaveDoChamado = (id: string) => [...CHAVE, id] as const;
const uuid = z.string().uuid();

const esquemaAprovador = z.object({
  usuario_id: uuid,
  nome: z.string(),
  interno: z.boolean(),
  decisao: z.enum(["aprovada", "recusada"]).nullable(),
  comentario: z.string().nullable(),
  decidido_em: z.string().nullable(),
});

const esquemaPedido = z.object({
  id: uuid,
  regra_nome: z.string(),
  estado: z.enum(["pendente", "aprovada", "recusada", "cancelada"]),
  minimo_aprovacoes: z.number().int(),
  aprovados: z.number().int(),
  pausa_o_sla: z.boolean(),
  criada_em: z.string(),
  decidida_em: z.string().nullable(),
  aprovadores: z.array(esquemaAprovador),
  pode_decidir: z.boolean(),
});

const esquemaPedidos = z.array(esquemaPedido);

export type PedidoDeAprovacao = z.infer<typeof esquemaPedido>;

export const ROTULO_DO_PEDIDO: Record<PedidoDeAprovacao["estado"], string> = {
  pendente: "Aguardando aprovação",
  aprovada: "Aprovado",
  recusada: "Recusado",
  cancelada: "Encerrado",
};

export function useAprovacoesDoChamado(ticketId: string) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "aprovacoes"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/${encodeURIComponent(ticketId)}/aprovacoes`, esquemaPedidos, { signal }),
    enabled: Boolean(ticketId),
  });
}

export function useDecidirAprovacao(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      pedidoId,
      aprovar,
      comentario,
    }: {
      pedidoId: string;
      aprovar: boolean;
      comentario: string;
    }) =>
      enviarJson(
        `/tickets/${encodeURIComponent(ticketId)}/aprovacoes/${encodeURIComponent(pedidoId)}/${
          aprovar ? "aprovar" : "recusar"
        }`,
        esquemaPedidos,
        { comentario: comentario.trim() || null },
      ),
    // A decisão muda o estado do chamado (recusa cancela) e o SLA: a tela toda se atualiza.
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE }),
  });
}

const esquemaPessoaAprovadora = z.object({
  id: uuid,
  nome: z.string(),
  email: z.string(),
  interno: z.boolean(),
});

const esquemaRegra = z.object({
  id: uuid,
  organizacao_id: uuid,
  nome: z.string(),
  urgencia: z.string().nullable(),
  categoria: z.string().nullable(),
  servico: z.string().nullable(),
  minimo_aprovacoes: z.number().int(),
  pausar_sla: z.boolean(),
  ativa: z.boolean(),
  aprovadores: z.array(esquemaPessoaAprovadora),
});

export type RegraDeAprovacao = z.infer<typeof esquemaRegra>;
export type PessoaAprovadora = z.infer<typeof esquemaPessoaAprovadora>;

export function useRegrasDeAprovacao(enabled = true) {
  return useQuery({
    queryKey: CHAVE_DAS_REGRAS,
    queryFn: ({ signal }) =>
      pedirJson("/tickets/aprovacoes/regras", z.array(esquemaRegra), { signal }),
    enabled,
  });
}

export function useCandidatosAAprovador(organizacaoId: string) {
  return useQuery({
    queryKey: ["aprovacoes", "candidatos", organizacaoId],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/aprovacoes/candidatos?organizacao_id=${encodeURIComponent(organizacaoId)}`,
        z.array(esquemaPessoaAprovadora),
        { signal },
      ),
    enabled: Boolean(organizacaoId),
  });
}

export interface PedidoDeRegra {
  organizacao_id: string;
  nome: string;
  urgencia: string | null;
  categoria: string | null;
  servico: string | null;
  minimo_aprovacoes: number;
  pausar_sla: boolean;
  aprovadores: string[];
}

export function useCriarRegraDeAprovacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: PedidoDeRegra) =>
      enviarJson("/tickets/aprovacoes/regras", esquemaRegra, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_REGRAS }),
  });
}

export function useAlterarRegraDeAprovacao() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Partial<PedidoDeRegra> & { ativa?: boolean } }) =>
      editarJson(`/tickets/aprovacoes/regras/${encodeURIComponent(id)}`, esquemaRegra, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_REGRAS }),
  });
}
